import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import {
  finalizeAllianceTaskPeriod,
  getAllianceDiplomacy,
  type AllianceTaskPeriodClient,
  type DiplomacyTargetInput,
} from '../../common/allianceApi'
import {
  ALLIANCE_TASK_DIPLOMACY_COUNTS,
  ALLIANCE_TASK_DIPLOMACY_TARGET_LEVELS,
  ALLIANCE_TASK_TITLE_STYLE,
} from '../../common/allianceTaskOptionsClient'
import type { CastleData } from '../../game/world-map/terrial-engine/types'
import { CastlePickerModal, DateTimeField } from './allianceAttackPlanShared'
import { alert } from '../Alert/Alert'

interface AllianceDiplomacyLevelModalProps {
  open: boolean
  address: string
  /** The leader's own alliance id — used to exclude own castles in the picker. */
  allianceId?: string
  period: AllianceTaskPeriodClient
  onBack: () => void
  onFinalized: () => void
}

type TargetLevel = 'ceasefire' | 'alliance' | 'marriage'

interface DiplomacyRow {
  allianceId: string
  allianceName: string
  targetLevel: TargetLevel | ''
  from: string // datetime-local value
  to: string
}

const AT = 'https://s0swaypl0zqnzq8t.public.blob.vercel-storage.com/redesign/alliance/alliance-task'
const MODAL_BG = `${AT}/modal-bg.webp`
const TAB_ACTIVE = `${AT}/tab-active-bg.webp`
const TAB_INACTIVE = `${AT}/tab-inactive-bg.webp`
const TEXT_BG = `${AT}/text-bg.webp`
const TITLE_BG = `${AT}/title-bg.webp`
const BACK_BTN = `${AT}/back-btn.webp`
const CONFIRM_BTN = `${AT}/confirm-btn.webp`
const DIPLO_ICON = `${AT}/voting-modal/diplomacy-icon.webp`
const CINZEL = '"Cinzel", "Trajan Pro", "Times New Roman", serif'

const labelStyle: React.CSSProperties = {
  fontFamily: CINZEL,
  fontWeight: 700,
  fontSize: '2cqw',
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: '#5a3c17',
}

function pad(n: number): string {
  return String(n).padStart(2, '0')
}
function toLocalInput(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

/** Capitalize a relationship tier for display. */
function tierLabel(t: (k: string, o?: Record<string, unknown>) => string, level: string): string {
  const key = `alliance.relationTier${level.charAt(0).toUpperCase()}${level.slice(1)}`
  const fallback = level.charAt(0).toUpperCase() + level.slice(1)
  return t(key, { defaultValue: fallback })
}

/**
 * Diplomacy task modal — shown after the leader picks the Diplomacy official task. Mirrors the
 * Conquer plan (tabs 1 / 2 / 3 alliances, map-based selection, a per-target time window) but with
 * NO attackers: the leader picks each alliance on the map, sees the current relationship tier, and
 * sets a target tier (ceasefire / alliance / marriage) to reach this season. Completing all raises
 * governance +5 / +10 / +20 by alliance count.
 */
export const AllianceDiplomacyLevelModal: React.FC<AllianceDiplomacyLevelModalProps> = ({
  open,
  address,
  allianceId,
  period,
  onBack,
  onFinalized,
}) => {
  const { t } = useTranslation()
  const counts = ALLIANCE_TASK_DIPLOMACY_COUNTS
  const [count, setCount] = useState<number | null>(null)
  const [rows, setRows] = useState<DiplomacyRow[]>([])
  const [levelByAllianceId, setLevelByAllianceId] = useState<Record<string, string>>({})
  const [pickerIndex, setPickerIndex] = useState<number | null>(null)
  const [busy, setBusy] = useState(false)

  const seasonMin = useMemo(
    () => toLocalInput(new Date(Math.max(Date.now(), new Date(period.periodStart).getTime()))),
    [period.periodStart],
  )
  const seasonMax = useMemo(() => toLocalInput(new Date(period.periodEnd)), [period.periodEnd])

  useEffect(() => {
    if (!open || !address) return
    let alive = true
    void getAllianceDiplomacy(address)
      .then((r) => {
        if (!alive) return
        const map: Record<string, string> = {}
        for (const rel of r.relationships || []) {
          if (rel.otherAllianceId) map[String(rel.otherAllianceId)] = rel.level || 'spirit'
        }
        setLevelByAllianceId(map)
      })
      .catch(() => {})
    return () => {
      alive = false
    }
  }, [open, address])

  const pickCount = useCallback(
    (n: number) => {
      setCount(n)
      setRows(
        Array.from({ length: n }, () => ({
          allianceId: '',
          allianceName: '',
          targetLevel: '' as TargetLevel | '',
          from: seasonMin,
          to: seasonMax,
        })),
      )
    },
    [seasonMin, seasonMax],
  )

  useEffect(() => {
    if (open && count === null && counts.length) pickCount(counts[0])
  }, [open, count, counts, pickCount])

  const updateRow = (i: number, patch: Partial<DiplomacyRow>) => {
    setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, ...patch } : r)))
  }

  const handlePicked = (castle: CastleData) => {
    if (pickerIndex === null) return
    const aid = castle.allianceId || ''
    if (aid && rows.some((r, idx) => idx !== pickerIndex && r.allianceId === aid)) {
      alert.open(t('alliance.diplomacyDupAlliance', { defaultValue: 'That alliance is already chosen.' }))
      return
    }
    updateRow(pickerIndex, {
      allianceId: aid,
      allianceName: castle.allianceName || t('alliance.conquerUnknownAlliance', { defaultValue: 'Enemy Alliance' }),
    })
    setPickerIndex(null)
  }

  if (!open || typeof document === 'undefined') return null

  const currentLevelOf = (id: string) => (id ? levelByAllianceId[id] || 'spirit' : null)

  const canSubmit =
    count !== null &&
    rows.length === count &&
    rows.every((r) => r.allianceId && r.targetLevel && r.from && r.to && new Date(r.from) < new Date(r.to))

  const handleConfirm = async () => {
    if (!canSubmit || busy || count === null) return
    setBusy(true)
    try {
      const diplomacyTargets: DiplomacyTargetInput[] = rows.map((r) => ({
        targetAllianceId: r.allianceId,
        targetDiplomacyLevel: r.targetLevel as TargetLevel,
        from: new Date(r.from).toISOString(),
        to: new Date(r.to).toISOString(),
      }))
      const res = await finalizeAllianceTaskPeriod(address, 'diplomacy', period._id, {
        diplomacyCount: count,
        diplomacyTargets,
      })
      const delta = res.governanceDelta ?? 0
      alert.open(
        t('alliance.taskFinalized', {
          defaultValue: 'Official task locked. Governance {{delta}}.',
          delta: delta > 0 ? `+${delta}` : String(delta),
        }),
      )
      onFinalized()
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } }; message?: string }
      alert.open(e?.response?.data?.message || e?.message || t('alliance.taskFinalizeFailed', { defaultValue: 'Could not lock the task.' }))
      setBusy(false)
    }
  }

  return createPortal(
    <div
      className="fixed inset-0 flex items-start justify-center overflow-y-auto bg-black/80 p-4"
      style={{ zIndex: 240 }}
      role="presentation"
    >
      <div
        className="relative my-auto w-full select-none"
        style={{
          width: 'min(97vw, 680px)',
          containerType: 'inline-size',
          backgroundImage: `url(${MODAL_BG})`,
          backgroundSize: '100% 100%',
          backgroundRepeat: 'no-repeat',
          padding: '4% 2% 1.5%',
          filter: 'drop-shadow(0 18px 52px rgba(0,0,0,0.7))',
        }}
        role="dialog"
        aria-modal="true"
      >
        {/* Title */}
        <h2 className="text-center" style={{ ...ALLIANCE_TASK_TITLE_STYLE, fontSize: '6cqw' }}>
          {t('alliance.taskOptionDiplomacy', { defaultValue: 'Diplomacy' })}
        </h2>
        <p
          className="text-center"
          style={{ fontFamily: CINZEL, fontWeight: 600, fontSize: '2.4cqw', color: '#6a4d2c', margin: '1.2cqw 0 0' }}
        >
          {t('alliance.diplomacySubtitle2', { defaultValue: 'Alliance Seasonal Plan for Diplomatic Relations' })}
        </p>
        <p
          className="text-center"
          style={{ fontFamily: CINZEL, fontWeight: 700, fontSize: '2.5cqw', color: '#4a3418', margin: '2.6cqw 0 0', textTransform: 'uppercase', letterSpacing: '0.04em' }}
        >
          {t('alliance.diplomacyHowMany', { defaultValue: 'Plan how many alliances to court this season' })}
        </p>

        {/* Count tabs */}
        <div className="flex items-stretch justify-center" style={{ marginTop: '2.4cqw', gap: '2.5cqw' }}>
          {counts.map((n) => {
            const active = count === n
            return (
              <button
                key={n}
                type="button"
                onClick={() => pickCount(n)}
                disabled={busy}
                className="relative transition-[filter,transform] hover:brightness-110 active:scale-[0.99] disabled:cursor-not-allowed"
                style={{
                  width: '27cqw',
                  height: '10cqw',
                  backgroundImage: `url(${active ? TAB_ACTIVE : TAB_INACTIVE})`,
                  backgroundSize: '100% 100%',
                  backgroundRepeat: 'no-repeat',
                  fontFamily: CINZEL,
                  fontWeight: 800,
                  fontSize: '3cqw',
                  textTransform: 'uppercase',
                  letterSpacing: '0.03em',
                  color: active ? '#fff2c8' : '#c8ab74',
                  textShadow: active ? '0 1px 2px rgba(30,15,4,0.9)' : 'none',
                  filter: active ? 'none' : 'brightness(0.92)',
                }}
              >
                {t('alliance.diplomacyNAlliances', { defaultValue: '{{n}} Alliance', n, count: n })}
              </button>
            )
          })}
        </div>

        {/* Alliance rows — fixed-height scroll region so the modal height stays constant. */}
        {count !== null ? (
          <div
            className="mt-[5cqw] flex min-w-0 flex-col overflow-y-auto"
            style={{ height: '54vh', paddingLeft: '0.5cqw', paddingRight: '2.5cqw', gap: '3cqw', marginTop: '2.5cqw', marginBottom: '2cqw' }}
          >
            {rows.map((row, i) => {
              const selected = !!row.allianceId
              const current = currentLevelOf(row.allianceId)
              return (
                <div
                  key={i}
                  className="flex min-w-0 flex-col"
                  style={{ paddingTop: '1.5cqw' }}
                >
                  {/* Banner title */}
                  <div className="flex items-center">
                    <div
                      className="relative flex shrink-0 items-center"
                      style={{
                        height: '8.6cqw',
                        width: '46cqw',
                        backgroundImage: `url(${TITLE_BG})`,
                        backgroundSize: '100% 100%',
                        backgroundRepeat: 'no-repeat',
                        paddingLeft: '1cqw',
                      }}
                    >
                      <img
                        src={DIPLO_ICON}
                        alt=""
                        draggable={false}
                        className="shrink-0 object-contain"
                        style={{ height: '10cqw', width: 'auto', marginRight: '2cqw', filter: 'drop-shadow(0 2px 3px rgba(0,0,0,0.55))' }}
                      />
                      <span
                        style={{ fontFamily: CINZEL, fontWeight: 800, fontSize: '3.4cqw', letterSpacing: '0.05em', textTransform: 'uppercase', color: '#f6e7c0', textShadow: '0 1px 2px rgba(20,10,3,0.8)' }}
                      >
                        {t('alliance.diplomacyAllianceN', { defaultValue: 'Alliance {{n}}', n: i + 1 })}
                      </span>
                    </div>
                    <div
                      className="flex-1"
                      style={{ height: '0.3cqw', marginLeft: '1.5cqw', background: 'linear-gradient(90deg, rgba(92,62,22,0.9), rgba(92,62,22,0.15))' }}
                    />
                  </div>

                  {/* Target alliance + select via map */}
                  <div className="mt-[4cqw] grid grid-cols-2" style={{ gap: '7cqw' }}>
                    <div className="flex flex-col" style={{ gap: '1cqw' }}>
                      <span style={labelStyle}>{t('alliance.diplomacyTargetAlliance', { defaultValue: 'Target Alliance' })}</span>
                      <div
                        className="flex items-center"
                        style={{
                          height: '9cqw',
                          backgroundImage: `url(${TEXT_BG})`,
                          backgroundSize: '100% 100%',
                          backgroundRepeat: 'no-repeat',
                          padding: '0 3cqw',
                        }}
                      >
                        <span
                          className="truncate"
                          style={{ fontFamily: CINZEL, fontWeight: 700, fontSize: '2.7cqw', color: selected ? '#f3ead0' : 'rgba(226,213,178,0.4)' }}
                        >
                          {selected ? row.allianceName : t('alliance.conquerNotSelected', { defaultValue: 'Not selected' })}
                        </span>
                      </div>
                    </div>

                    <div className="flex flex-col" style={{ gap: '1cqw' }}>
                      <span style={labelStyle}>{t('alliance.diplomacySelectAlliance', { defaultValue: 'Select Alliance' })}</span>
                      <button
                        type="button"
                        onClick={() => setPickerIndex(i)}
                        disabled={busy}
                        className="flex items-center justify-center transition-[filter] hover:brightness-110 disabled:cursor-not-allowed"
                        style={{
                          height: '9cqw',
                          backgroundImage: `url(${TEXT_BG})`,
                          backgroundSize: '100% 100%',
                          backgroundRepeat: 'no-repeat',
                          padding: '0 3cqw',
                          fontFamily: CINZEL,
                          fontWeight: 800,
                          fontSize: '2.7cqw',
                          textTransform: 'uppercase',
                          letterSpacing: '0.03em',
                          color: '#f0d488',
                        }}
                      >
                        <span className="truncate">{t('alliance.diplomacySelectAlliance', { defaultValue: 'Select Alliance' })}</span>
                      </button>
                    </div>
                  </div>

                  {/* Current level + target level */}
                  <div className="mt-[4cqw] grid grid-cols-2" style={{ gap: '7cqw' }}>
                    <div className="flex flex-col" style={{ gap: '1cqw' }}>
                      <span style={labelStyle}>{t('alliance.diplomacyCurrentLevel', { defaultValue: 'Current Level' })}</span>
                      <div
                        className="flex items-center"
                        style={{
                          height: '9cqw',
                          backgroundImage: `url(${TEXT_BG})`,
                          backgroundSize: '100% 100%',
                          backgroundRepeat: 'no-repeat',
                          padding: '0 3cqw',
                        }}
                      >
                        <span
                          className="truncate"
                          style={{ fontFamily: CINZEL, fontWeight: 700, fontSize: '2.7cqw', color: current ? '#f3ead0' : 'rgba(226,213,178,0.4)' }}
                        >
                          {current ? tierLabel(t, current) : t('alliance.diplomacyPickFirst', { defaultValue: 'Pick alliance first' })}
                        </span>
                      </div>
                    </div>

                    <div className="flex flex-col" style={{ gap: '1cqw' }}>
                      <span style={labelStyle}>{t('alliance.diplomacyTargetLevel', { defaultValue: 'Target Level' })}</span>
                      <select
                        value={row.targetLevel}
                        onChange={(e) => updateRow(i, { targetLevel: e.target.value as TargetLevel })}
                        disabled={busy}
                        style={{
                          height: '9cqw',
                          width: '100%',
                          padding: '0 3cqw',
                          background: 'rgba(12,8,4,0.72)',
                          border: '0.4cqw solid rgba(201,162,75,0.55)',
                          borderRadius: '1.6cqw',
                          color: '#f3ead0',
                          fontFamily: CINZEL,
                          fontWeight: 700,
                          fontSize: '2.7cqw',
                          outline: 'none',
                          colorScheme: 'dark',
                        }}
                      >
                        <option value="">{t('alliance.diplomacyPickLevel', { defaultValue: 'Choose target…' })}</option>
                        {ALLIANCE_TASK_DIPLOMACY_TARGET_LEVELS.map((lvl) => (
                          <option key={lvl.level} value={lvl.level}>
                            {t(lvl.i18nKey, { defaultValue: lvl.label })}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Time window */}
                  <div className="mt-[4cqw] grid grid-cols-2" style={{ gap: '7cqw' }}>
                    <div className="flex flex-col" style={{ gap: '1cqw' }}>
                      <span style={labelStyle}>{t('alliance.diplomacyFrom', { defaultValue: 'From' })}</span>
                      <DateTimeField
                        value={row.from}
                        min={seasonMin}
                        max={seasonMax}
                        disabled={busy}
                        onChange={(v) => updateRow(i, { from: v })}
                      />
                    </div>
                    <div className="flex flex-col" style={{ gap: '1cqw' }}>
                      <span style={labelStyle}>{t('alliance.diplomacyTo', { defaultValue: 'Until' })}</span>
                      <DateTimeField
                        value={row.to}
                        min={seasonMin}
                        max={seasonMax}
                        disabled={busy}
                        onChange={(v) => updateRow(i, { to: v })}
                      />
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        ) : null}

        {/* Footer buttons */}
        <div className="mt-[6cqw] flex items-stretch" style={{ gap: '3cqw' }}>
          <button
            type="button"
            onClick={onBack}
            disabled={busy}
            className="transition-[filter,transform] hover:brightness-110 active:scale-[0.99] disabled:opacity-50"
            style={{
              width: '32%',
              height: '11cqw',
              backgroundImage: `url(${BACK_BTN})`,
              backgroundSize: '100% 100%',
              backgroundRepeat: 'no-repeat',
              fontFamily: CINZEL,
              fontWeight: 800,
              fontSize: '3cqw',
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              color: '#f3ead0',
              textShadow: '0 1px 2px rgba(30,15,4,0.85)',
            }}
          >
            {t('common.back', { defaultValue: 'Back' })}
          </button>
          <button
            type="button"
            onClick={() => void handleConfirm()}
            disabled={!canSubmit || busy}
            className="flex-1 transition-[filter,transform] hover:brightness-110 active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-45"
            style={{
              height: '11cqw',
              backgroundImage: `url(${CONFIRM_BTN})`,
              backgroundSize: '100% 100%',
              backgroundRepeat: 'no-repeat',
              fontFamily: CINZEL,
              fontWeight: 800,
              fontSize: '3.2cqw',
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              color: '#fff2c8',
              textShadow: '0 1px 2px rgba(30,15,4,0.9)',
            }}
          >
            {busy
              ? t('common.loading', { defaultValue: 'Loading...' })
              : t('alliance.diplomacyConfirm', { defaultValue: 'Lock Diplomacy Plan' })}
          </button>
        </div>
      </div>

      <CastlePickerModal
        open={pickerIndex !== null}
        address={address}
        ownAllianceId={allianceId}
        onSelect={handlePicked}
        onClose={() => setPickerIndex(null)}
        leadersOnly
        title={t('alliance.diplomacySelectAllianceTitle', { defaultValue: 'Select an Alliance' })}
        hint={t('alliance.diplomacySelectAllianceHint', { defaultValue: 'Tap an alliance leader’s castle on the map to choose its alliance.' })}
      />
    </div>,
    document.body,
  )
}

export default AllianceDiplomacyLevelModal
